import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    useTheme,
} from '@mui/material';
import MarkdownIt from 'markdown-it';
import hljs from 'highlight.js/lib/core';
import javascript from 'highlight.js/lib/languages/javascript';
import python from 'highlight.js/lib/languages/python';
import json from 'highlight.js/lib/languages/json';
import bash from 'highlight.js/lib/languages/bash';

// Markdown layout styles (tables, headers, etc.)
import 'github-markdown-css/github-markdown.css';
import '../markdown-styles.css'; // Custom styles for markdown
import 'highlight.js/styles/github.css'; // Light mode
import 'highlight.js/styles/github-dark.css'; // Dark mode

import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningIcon from '@mui/icons-material/Warning';

// Register commonly used languages
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('python', python);
hljs.registerLanguage('json', json);
hljs.registerLanguage('bash', bash);

const TypingMarkdown = ({ content = "", className, isDark }) => {
    const md = new MarkdownIt({
        html: true,
        linkify: true,
        typographer: false,
        highlight: function (str, lang) {
            if (lang && hljs.getLanguage(lang)) {
                try {
                    return `<pre><code class="hljs">${hljs.highlight(str, { language: lang }).value}</code></pre>`;
                } catch (_) { }
            }
            return `<pre><code class="hljs">${hljs.highlightAuto(str).value}</code></pre>`;
        }
    });

    return (
        <Box className={`rk-markdown-body ${isDark ? 'hljs-dark' : 'hljs-light'}`}
            sx={{ width: '100%', px: 2, mr: 2, mb: 1 }}>
            <div dangerouslySetInnerHTML={{ __html: md.render(content) }} />
        </Box>
    );
};

const SystemGeneratedMarkdown = ({ type, config = { title: '', content: [{ text: '' }] } }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        // Dynamically load the correct highlight.js theme
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = isDark
            ? 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.7.0/styles/github-dark.min.css'
            : 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.7.0/styles/github.min.css';
        document.head.appendChild(link);

        return () => {
            document.head.removeChild(link);
        };
    }, [isDark]);

    const handleCopy = () => {
        navigator.clipboard.writeText(config.content[0].text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box
            sx={{
                mt: 1,
                mb: 1,
                minHeight: 100,
                borderRadius: 1,
                boxShadow: 1,
                background: isDark
                    ? 'linear-gradient(to top, rgb(47, 41, 54), rgb(36, 30, 41))'
                    : 'linear-gradient(to top, rgba(255,255,255,0.9), rgba(228, 243, 255, 0.9))',
            }}
        >

            {type === 'text' && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        position: 'relative',
                        top: 12,
                        px: 2,
                        mb: -3
                    }}
                >
                    <Typography variant="h6" fontWeight="bold">
                        {config.title}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={copied ? "Copied!" : "Copy"}>
                            <IconButton size="small" color="primary" onClick={handleCopy}>
                                {copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            )}


            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 2,
                    borderRadius: 1,
                    px: 2,
                    py: 2,
                }}
            >
                {type === 'error' && (
                    <Box
                        sx={{
                            background: 'linear-gradient(135deg, #FF6347 0%, #CC4F3A 100%)',
                            width: 50,
                            height: 100,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderRadius: 1,
                        }}
                    >
                        <WarningIcon sx={{ fontSize: 35, color: 'white' }} />
                    </Box>
                )}

                <TypingMarkdown
                    content={config.content[0].text}
                    isDark={isDark}
                />
            </Box>
        </Box>
    );
};

export default SystemGeneratedMarkdown;